import {
  ConnectionState,
  eStreamConnectionStateAtom,
  getFormatter,
  ListedInstrument,
  useDictionary,
  useRealtimeCurrencyUpdates,
} from '@eih-netbroker/common';
import { Popover } from '@mui/material';
import { useAtomValue } from 'jotai/index';
import React, { MouseEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as ExpandMoreIcon } from '../../../assets/icons/arrow-down-s-line.svg';
import { ReactComponent as ExpandLessIcon } from '../../../assets/icons/arrow-up-s-line.svg';
import { ReactComponent as BarChartIcon } from '../../../assets/icons/bar-chart.svg';
import { ReactComponent as PaymentsIcon } from '../../../assets/icons/bill-accounts-filled.svg';
import { ReactComponent as EyeIcon } from '../../../assets/icons/eye-fill.svg';
import { ReactComponent as EyeStrikeIcon } from '../../../assets/icons/eye-strike-fill.svg';
import { ReactComponent as NotificationsIcon } from '../../../assets/icons/notification-fill.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search-line.svg';
import { ReactComponent as ErsteLogo } from '../../../assets/svg/erste_logo.svg';
import { ReactComponent as ErsteIcon } from '../../../assets/svg/erste_logo_icon.svg';
import { useDialog } from '../../../context/DialogContext';
import { useLocale } from '../../../context/NbLocalizationContext';
import { NbButton } from '../../../shared/components/button/Button';
import { NbIconButton } from '../../../shared/components/icon-button/IconButton';
import { InstrumentSearchModal } from '../../../shared/components/instrument-search-modal/InstrumentSearchModal';
import { NbIndefiniteLoader, NbLoader } from '../../../shared/components/loader/Loader';
import { MoneyMaskHOC } from '../../../shared/components/money-mask/MoneyMask';
import { OverflowTooltip } from '../../../shared/components/tooltip/OverflowTooltip';
import { NbTooltip } from '../../../shared/components/tooltip/Tooltip';
import { Config } from '../../../shared/config/config';
import { useEStream } from '../../../shared/services/sse/useEStream';
import { useAccounts } from '../../../shared/services/useAccounts';
import { useUserSettings } from '../../../shared/services/user-settings/useUserSettings';
import { store } from '../../../shared/state/store';
import { featureFlagUtils } from '../../../shared/utils/FeatureFlagUtils';
import { cssWhen } from '../../../shared/utils/themeUtils';
import { MessageCenterModal } from '../../message/component/MessageCenterModal';
import { useNotification } from '../../message/services/useNotification';
import { NbNotificationCounter } from '../components/NotificationCounter';
import { SubscriptionExchangePopoverContent } from '../components/SubscriptionExchangePopoverContent';
import { CashPopoverEmptyContent } from '../components/cash-popover/CashPopoverEmptyContent';
import { StandaloneCurrencyExchangeSourceAndTargetCurrencyManagerProvider } from '../components/cash-popover/CurrencyExchangeSourceAndTargetCurrencyManagerContext';
import { EmptyCashPopoverContent } from '../components/cash-popover/EmptyCashPopoverContent';
import { useHeaderBalance } from '../services/useHeaderBalance';
import classes from './Header.module.scss';

export interface NbHeaderProps {
  isMenuCollapsed: boolean;
}

export const NbHeader = ({ isMenuCollapsed }: NbHeaderProps) => {
  const { translation } = useLocale();
  const { openDialog } = useDialog();
  const {
    initAccountSelectorButton,
    openAccountSelector,
    selectedClientName,
    isAccountSelectorOpen,
    selectedAccountOrClient,
  } = useAccounts();
  const [exchangePopover, setExchangePopover] = useState<HTMLButtonElement | null>(null);
  const [cashPopover, setCashPopover] = useState<HTMLButtonElement | null>(null);
  const eStreamConnectionState = useAtomValue(eStreamConnectionStateAtom);
  const { formatCurrency } = getFormatter();
  const accountSelectorButton = useRef<HTMLButtonElement | null>(null);
  const { unreadMessagesCount } = useNotification();
  const [isCashBalanceOpen, setIsCashBalanceOpen] = useState(false);
  const { subscribeToInstrumentChanges } = useEStream();
  const { cashInfo, accountBalanceValue, floatingValue } = useHeaderBalance();
  const cashPopoverButtonRef = useRef<HTMLButtonElement>(null);
  const { formatDictionaryType } = useDictionary();
  const accountCategoryName = useMemo(
    () =>
      selectedAccountOrClient.account
        ? formatDictionaryType(selectedAccountOrClient.account.accountCategory, 'accountCategoryTypes')
        : translation.header.allAccounts,
    [formatDictionaryType, selectedAccountOrClient.account, translation.header.allAccounts],
  );
  const { isUserMoneyVisible, updateUserMoneyVisibilty } = useUserSettings();

  const onSearchInstrument = async () => {
    await openDialog<ListedInstrument>((close: (params?: ListedInstrument) => void) => (
      <InstrumentSearchModal onCancel={() => close()} />
    ));
  };

  const showAlertModal = async () => {
    await openDialog((close: () => void) => <MessageCenterModal onCancel={close} />);
  };

  const handleAccountsClick = () => {
    openAccountSelector();
  };

  const handleCashClose = () => {
    setIsCashBalanceOpen(false);
  };

  const handleCashOnClick = () => {
    setIsCashBalanceOpen(true);
  };

  const handleCashClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (cashInfo.isLoading) {
      return;
    }

    setCashPopover(event.currentTarget);
    setIsCashBalanceOpen(!isCashBalanceOpen);
  };

  const handleExchangeClose = () => {
    setExchangePopover(null);
  };

  const handleExchangeClick = (event: MouseEvent<HTMLButtonElement>) => {
    setExchangePopover(event.currentTarget);
  };

  const toggleUserMoneyVisibility = () => {
    updateUserMoneyVisibilty(!isUserMoneyVisible);
  };

  const floatingValueTooltip = floatingValue.isLoaded && (
    <div>
      {translation.header.floatingResult}:{' '}
      <MoneyMaskHOC
        isInline
        size={100}
      >
        {formatCurrency(floatingValue.value, Config.NetBroker.BaseCurrency)}
      </MoneyMaskHOC>
      <br />
      <br />
      {translation.header.floatingResultTooltip}
    </div>
  );

  const inventoryInHufTooltip = cashInfo.isLoaded && (
    <div>
      {translation.header.cashBalance}:{' '}
      <MoneyMaskHOC
        isInline
        size={100}
      >
        {formatCurrency(cashInfo.inventoryInHuf, Config.NetBroker.BaseCurrency)}
      </MoneyMaskHOC>
    </div>
  );

  useEffect(() => {
    if (accountSelectorButton.current) {
      initAccountSelectorButton(accountSelectorButton.current);
    }
  }, [initAccountSelectorButton]);

  const [estreamSSEDebugger, setEstreamSSEDebugger] = useState<ReactElement>();

  useEffect(() => {
    if (featureFlagUtils.isEStreamStatsSimple) {
      import('../../../shared/components/app-debug/EStreamSSEHeartBeat').then(({ default: EStreamSSEHeartBeat }) => {
        setEstreamSSEDebugger(<EStreamSSEHeartBeat />);
      });
    }
  }, []);

  // Handle portfolio related side effects, like updating real time currency exchange rates
  useRealtimeCurrencyUpdates({
    store: store,
    subscribeToInstrumentChanges,
  });

  return (
    <div className={cssWhen(classes.header, [isMenuCollapsed, classes.headerIsCollapsedMenu])}>
      <div
        className={cssWhen(classes.logo, [isMenuCollapsed, classes.logoIsCollapsedMenu])}
        data-testid="erste-logo"
      >
        <ErsteLogo />
        <ErsteIcon />
      </div>
      {estreamSSEDebugger && (
        <React.Suspense fallback={<div />}>{estreamSSEDebugger && estreamSSEDebugger}</React.Suspense>
      )}

      <div className={cssWhen(classes.main, [isMenuCollapsed, classes.mainIsCollapsedMenu])}>
        <NbButton
          color={'secondary'}
          variant={'outlined'}
          onClick={handleAccountsClick}
          className={cssWhen(classes.headerButtonMain, [isAccountSelectorOpen, classes.headerButtonMainIsOpen])}
          ref={accountSelectorButton}
          data-testid="account-selector-button"
        >
          <PaymentsIcon className={classes.headerButtonMainIcon} />

          <OverflowTooltip title={<span data-hj-suppress>{`${selectedClientName} - ${accountCategoryName}`}</span>}>
            <div className={classes.headerButtonMainContent}>
              <span data-hj-suppress>{selectedClientName}</span>
              <span>{accountCategoryName}</span>
            </div>
          </OverflowTooltip>

          {isAccountSelectorOpen ? (
            <ExpandLessIcon className={classes.headerButtonAllAccountsIcon} />
          ) : (
            <ExpandMoreIcon className={classes.headerButtonAllAccountsIcon} />
          )}
        </NbButton>

        {/*{everythingLoaded && (*/}
        {accountBalanceValue.isLoading && (
          <div className={classes.mainItem}>
            <NbIndefiniteLoader isTransparent={true} />
            <span>{translation.header.accountBalanceValue}</span>
          </div>
        )}

        {accountBalanceValue.isLoaded && (
          <NbTooltip title={<div dangerouslySetInnerHTML={{ __html: translation.header.accountBalanceTooltip }}></div>}>
            <div className={classes.mainItem}>
              <MoneyMaskHOC>
                <span
                  className={classes.mainItemNumber}
                  data-testid="header-account-value"
                >
                  {formatCurrency(accountBalanceValue.value, Config.NetBroker.BaseCurrency)}
                </span>
              </MoneyMaskHOC>
              <span>{translation.header.accountBalanceValue}</span>
            </div>
          </NbTooltip>
        )}

        {floatingValue.isLoading && (
          <div className={classes.mainItem}>
            <NbIndefiniteLoader isTransparent={true} />
            <span>{translation.header.floatingResult}</span>
          </div>
        )}
        {floatingValue.isLoaded && (
          <NbTooltip title={floatingValueTooltip}>
            <div className={classes.mainItem}>
              <MoneyMaskHOC>
                <span
                  className={classes.mainItemNumber}
                  data-testid="header-floating-value"
                >
                  {formatCurrency(floatingValue.value, Config.NetBroker.BaseCurrency)}
                </span>
              </MoneyMaskHOC>
              <span>{translation.header.floatingResult}</span>
            </div>
          </NbTooltip>
        )}

        <NbButton
          color={'secondary'}
          variant={'outlined'}
          onClick={handleCashClick}
          ref={cashPopoverButtonRef}
          className={cssWhen(classes.headerButtonMain, [isCashBalanceOpen, classes.headerButtonMainIsOpen])}
          data-testid="cash-balance-button"
        >
          <OverflowTooltip title={inventoryInHufTooltip}>
            <div className={`${classes.headerButtonMainContent} ${classes.headerButtonMainContentIsSmall}`}>
              {cashInfo.isLoaded && (
                <MoneyMaskHOC isInverse={isCashBalanceOpen}>
                  <span
                    className={classes.headerButtonMainContentNumber}
                    data-testid="header-cash-balance"
                  >
                    {formatCurrency(cashInfo.inventoryInHuf, Config.NetBroker.BaseCurrency)}
                  </span>
                </MoneyMaskHOC>
              )}
              {!cashInfo.isLoaded && <NbIndefiniteLoader isTransparent={true} />}
              <span>{translation.header.cashBalance}</span>
            </div>
          </OverflowTooltip>
          {isCashBalanceOpen ? (
            <ExpandLessIcon className={classes.headerButtonAllAccountsIcon} />
          ) : (
            <ExpandMoreIcon className={classes.headerButtonAllAccountsIcon} />
          )}
        </NbButton>

        <Popover
          open={!!cashPopover && isCashBalanceOpen}
          anchorEl={cashPopover}
          onClose={handleCashClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          classes={{ paper: classes.popover }}
        >
          {cashInfo.isLoading && <NbIndefiniteLoader isTransparent={true} />}

          {cashInfo.isLoaded && cashInfo.inventoryInHuf > 0 && (
            <StandaloneCurrencyExchangeSourceAndTargetCurrencyManagerProvider
              inventoryInHuf={cashInfo.inventoryInHuf}
              fiats={cashInfo.cashInfo}
              handleCashClose={handleCashClose}
              handleCashOnClick={handleCashOnClick}
            />
          )}

          {/* TODO: Delete this block after feature release */}
          {cashInfo.isLoaded && cashInfo.inventoryInHuf <= 0 && !Config.FeatureFlags.isCashActionEnabled && (
            <EmptyCashPopoverContent />
          )}

          {cashInfo.isLoaded && cashInfo.inventoryInHuf <= 0 && Config.FeatureFlags.isCashActionEnabled && (
            <CashPopoverEmptyContent handleCashClose={handleCashClose} />
          )}
        </Popover>
      </div>

      <div className={classes.actions}>
        <NbButton
          data-testid="header-instrument-search"
          onClick={onSearchInstrument}
          className={classes.actionsSearch}
        >
          <SearchIcon /> {translation.header.search}
        </NbButton>
        <div className={classes.actionWithNoti}>
          <NbIconButton
            icon={isUserMoneyVisible ? <EyeIcon /> : <EyeStrikeIcon />}
            onClick={toggleUserMoneyVisibility}
          />
        </div>
        <div className={classes.actionWithNoti}>
          <NbIconButton
            icon={<NotificationsIcon />}
            onClick={showAlertModal}
          />
          {unreadMessagesCount > 0 && (
            <NbNotificationCounter isLong={unreadMessagesCount > 9}>
              {unreadMessagesCount < 10 ? unreadMessagesCount : '9+'}
            </NbNotificationCounter>
          )}
        </div>

        <div
          className={classes.actionWithNoti}
          data-id={`sse-state-indicator-${eStreamConnectionState}`}
        >
          <NbIconButton
            icon={<BarChartIcon />}
            onClick={handleExchangeClick}
          />
          {(eStreamConnectionState === ConnectionState.Disconnected ||
            eStreamConnectionState === ConnectionState.FailedToConnected) && (
            <NbNotificationCounter>!</NbNotificationCounter>
          )}
          {eStreamConnectionState === ConnectionState.Connected && (
            <NbNotificationCounter
              mode={'green'}
              size={'small'}
              className={classes.hideLater}
            >
              &nbsp;
            </NbNotificationCounter>
          )}
          {eStreamConnectionState === ConnectionState.Connecting && (
            <span className={classes.sseStateIndicator}>
              <NbLoader size={'small'} />
            </span>
          )}

          <Popover
            open={!!exchangePopover}
            anchorEl={exchangePopover}
            onClose={handleExchangeClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            classes={{ paper: classes.exchangeStatusPopover }}
          >
            <SubscriptionExchangePopoverContent onHandleExchangeClose={handleExchangeClose} />
          </Popover>
        </div>
      </div>
    </div>
  );
};
